import { useCallback } from 'react';
import type { AddNoiseValue } from '../../lib/filters/add-noise';
import type { AdjustmentPanelProps } from '../adjustments/registry';
import { RangeRow } from '../adjustments/RangeRow';

/** Add Noise panel — amount slider, distribution chips, mono toggle,
 *  and a Re-roll button that bumps the seed so the user can shuffle
 *  the noise pattern without changing any other parameter.
 *
 *  The seed lives on the config (and ends up persisted in the layer
 *  for filter-layer applies) — both the destructive preview and the
 *  non-destructive layer composite need the same pattern across
 *  re-applies, which is exactly what the non-cumulative contract
 *  guarantees. */
export function AddNoisePanel({ value, onChange }: AdjustmentPanelProps<AddNoiseValue>) {
  const set = useCallback(
    (patch: Partial<AddNoiseValue>) => onChange({ ...value, ...patch }),
    [onChange, value],
  );

  const reroll = useCallback(() => {
    // Bump the seed by a random uint32 each click. Doesn't need to
    // be cryptographic — just different from the current value so
    // the hash output shifts visibly.
    const next = ((Math.random() * 0xffffffff) | 0) >>> 0;
    onChange({ ...value, seed: next === value.seed ? next + 1 : next });
  }, [onChange, value]);

  return (
    <div className="space-y-3">
      <RangeRow
        label="Amount"
        min={0}
        max={100}
        step={1}
        value={value.amount}
        onChange={(v) => set({ amount: v })}
      />
      <DistributionRow value={value.distribution} onChange={(d) => set({ distribution: d })} />
      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 cursor-pointer accent-primary"
          checked={value.monochromatic}
          onChange={(e) => set({ monochromatic: e.target.checked })}
        />
        Monochromatic
      </label>
      <div className="flex items-center gap-2">
        <span className="text-xs w-16 text-muted-foreground">Seed</span>
        <span className="flex-1 text-xs tabular-nums text-muted-foreground">{value.seed}</span>
        <button
          type="button"
          onClick={reroll}
          className="px-2 py-1 text-xs rounded-md border border-border text-foreground hover:bg-accent"
          title="Shuffle the noise pattern without changing other parameters"
        >
          Re-roll
        </button>
      </div>
    </div>
  );
}

function DistributionRow({
  value,
  onChange,
}: {
  value: AddNoiseValue['distribution'];
  onChange: (next: AddNoiseValue['distribution']) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span id="noise-distribution-label" className="text-xs w-16 text-muted-foreground">
        Type
      </span>
      <div
        role="radiogroup"
        aria-labelledby="noise-distribution-label"
        className="flex gap-1 flex-1"
      >
        {(['uniform', 'gaussian'] as const).map((d) => {
          const active = value === d;
          return (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(d)}
              className={`flex-1 px-2 py-1 text-xs rounded-md border capitalize transition-colors ${
                active
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}
